/**
 * StageTargetFieldContainer — the per-stage time-in-stage target field
 * (SE-5497).
 *
 * Holds a local draft so the user can type freely, and commits the PARSED value
 * on blur / submit rather than on every keystroke (one write per edit). The
 * parent (StageEditorRowView) reseeds this field by KEY
 * (`${stageId}-${timeTargetDays}`) whenever the committed value changes — a
 * remount, never a reset effect (the repo's set-state-in-effect rule). Empty
 * clears the target; entries floor to a minimum of 1 via
 * {@link parseTimeTargetInput}.
 * @module features/player-kanban/components/molecules/StageTargetField/StageTargetFieldContainer
 */

import { useCallback, useRef, useState } from "react";

import StageTargetFieldView from "./StageTargetFieldView";

import { parseTimeTargetInput } from "../../../utils/stageTime";

/** Props for {@link StageTargetFieldContainer}. */
export interface StageTargetFieldContainerProps {
  /** The stage this field targets. */
  readonly stageId: string;
  /** Stage name (for the field's accessibility label). */
  readonly stageName: string;
  /** The committed target in days (null = none) — seeds the draft. */
  readonly timeTargetDays: number | null;
  /** Whether a write for this row is in flight (disables the input). */
  readonly isBusy: boolean;
  /** Commit handler for the parsed target. */
  readonly onChangeTarget: (stageId: string, next: number | null) => void;
}

/**
 * Stateful wrapper for the time-target input.
 * @param root0 - {@link StageTargetFieldContainerProps}.
 * @param root0.stageId - The stage this field targets.
 * @param root0.stageName - Stage name (for the field's accessibility label).
 * @param root0.timeTargetDays - The committed target in days (null = none).
 * @param root0.isBusy - Whether a write for this row is in flight.
 * @param root0.onChangeTarget - Commit handler for the parsed target.
 * @returns The rendered field.
 */
const StageTargetFieldContainer = ({
  stageId,
  stageName,
  timeTargetDays,
  isBusy,
  onChangeTarget,
}: StageTargetFieldContainerProps) => {
  const [draft, setDraft] = useState(
    timeTargetDays == null ? "" : String(timeTargetDays)
  );
  // Submit fires blur too, so onCommit can arrive twice for one interaction;
  // focusing then blurring without typing also fires it. Only dispatch when the
  // draft actually changed since the last commit — at most one write per edit.
  const isDirtyRef = useRef(false);

  const handleDraftChange = useCallback((next: string) => {
    isDirtyRef.current = true;
    setDraft(next);
  }, []);

  const handleCommit = useCallback(() => {
    if (!isDirtyRef.current) return;
    isDirtyRef.current = false;
    onChangeTarget(stageId, parseTimeTargetInput(draft));
  }, [onChangeTarget, stageId, draft]);

  return (
    <StageTargetFieldView
      stageId={stageId}
      stageName={stageName}
      draft={draft}
      isBusy={isBusy}
      onDraftChange={handleDraftChange}
      onCommit={handleCommit}
    />
  );
};

StageTargetFieldContainer.displayName = "StageTargetFieldContainer";

export default StageTargetFieldContainer;
